import { useEffect, useRef, useState } from "react";
import { PERIODI_RAPIDI, periodoCorrente, periodoRapido, type PeriodoId } from "../dateRange";
import { IconCalendar, IconCheck } from "./icons";

/** «24/08/2026» da «2026-08-24» (il valore di `<input type="date">`). */
function dataBreve(iso: string): string {
  const [anno, mese, giorno] = iso.split("-");
  return anno && mese && giorno ? `${giorno}/${mese}/${anno}` : iso;
}

/** Il periodo per NOME quando e' un periodo rapido («Ultimi 30 giorni»),
 *  altrimenti le sue due date; la fine vuota arriva a oggi. */
function etichettaPeriodo(corrente: PeriodoId | null, from: string, to: string): string {
  const rapido = PERIODI_RAPIDI.find((p) => p.id === corrente);
  if (rapido) return rapido.label;
  return `${from ? dataBreve(from) : "…"} → ${to ? dataBreve(to) : "oggi"}`;
}

interface PeriodoFiltroProps {
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (v: string) => void;
  onDateToChange: (v: string) => void;
}

/** Il calendario della lista ordini (Davide 2026-09-23: «aggiungi al
 *  calendario delle scelte di default, ultimi 30 gg, ultimo anno ecc»).
 *  Un tasto solo, che dice il periodo per nome, al posto dei due campi data
 *  sempre aperti: le scelte rapide sono un clic, il periodo su misura resta
 *  nel menu con le sue due date. */
export function PeriodoFiltro({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
}: PeriodoFiltroProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const corrente = periodoCorrente(dateFrom, dateTo);
  const etichetta = etichettaPeriodo(corrente, dateFrom, dateTo);

  useEffect(() => {
    const fuori = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", fuori);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fuori);
      document.removeEventListener("keydown", esc);
    };
  }, []);

  function scegli(id: PeriodoId) {
    const date = periodoRapido(id);
    onDateFromChange(date.from);
    onDateToChange(date.to);
    setOpen(false);
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label={`Periodo: ${etichetta}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex h-[34px] items-center gap-1.5 rounded-full border border-line-2 bg-card px-3 text-[12.5px] transition-colors hover:border-ink-4"
      >
        <IconCalendar className="w-[13px] h-[13px] text-ink-3 fill-none stroke-current stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round] shrink-0" />
        <span className="whitespace-nowrap font-medium tabular-nums text-ink">{etichetta}</span>
        <svg
          className="w-3 h-3 fill-none stroke-current stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round] text-ink-3"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Periodo degli ordini"
          className="absolute top-[calc(100%+6px)] left-0 z-[60] w-[250px] rounded-lg border border-line-2 bg-card py-1.5 shadow-popover"
        >
          {PERIODI_RAPIDI.map((p) => {
            const scelto = corrente === p.id;
            return (
              <button
                key={p.id}
                type="button"
                role="menuitemradio"
                aria-checked={scelto}
                onClick={() => scegli(p.id)}
                className="flex w-full items-center gap-2 px-3 py-[7px] text-left text-[12.5px] transition-colors hover:bg-paper-2"
              >
                <span className={scelto ? "font-medium text-ink" : "text-ink-2"}>{p.label}</span>
                {scelto && (
                  <IconCheck className="w-[11px] h-[11px] ml-auto fill-none stroke-ink stroke-[3] [stroke-linecap:round] [stroke-linejoin:round]" />
                )}
              </button>
            );
          })}
          {/* Periodo su misura: le due date di sempre. Campo vuoto = nessun
              limite su quel lato; la fine vuota arriva sempre a oggi. */}
          <div className="mt-1 flex flex-col gap-1.5 border-t border-line px-3 pb-1 pt-2.5">
            <label className="flex items-center justify-between gap-2 text-[12px] text-ink-3">
              Dal
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => onDateFromChange(e.target.value)}
                aria-label="Data inizio"
                className="w-[140px] rounded-md border border-line bg-paper px-2 py-1 font-mono text-[12px] tabular-nums text-ink outline-none focus:border-ink-4"
              />
            </label>
            <label className="flex items-center justify-between gap-2 text-[12px] text-ink-3">
              Al
              <input
                type="date"
                value={dateTo}
                onChange={(e) => onDateToChange(e.target.value)}
                aria-label="Data fine"
                className="w-[140px] rounded-md border border-line bg-paper px-2 py-1 font-mono text-[12px] tabular-nums text-ink outline-none focus:border-ink-4"
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
